// src/pages/CourseTime/hooks/useAssessmentTooltips.js
import { useState, useEffect, useMemo } from 'react';

// Utility functions from QuestionPoster
function getIndexOf(searchStr, str, caseSensitive, startIndex = 0) {
  let searchStrLen = searchStr.length;
  if (searchStrLen === 0) {
    return -1;
  }
  if (!caseSensitive) {
    str = str.toLowerCase();
    searchStr = searchStr.toLowerCase();
  }
  return str.indexOf(searchStr, startIndex);
}

function replaceKeyword(searchStr, replaceStr, parentStr, caseSensitive = false) {
  let startIndex = 0;
  let index = -1;
  while ((index = getIndexOf(searchStr, parentStr, caseSensitive, startIndex)) > -1) {
    const firstStr = parentStr.slice(0, index);
    const lastStr = parentStr.slice(index + searchStr.length, parentStr.length);
    parentStr = firstStr + replaceStr + lastStr;
    startIndex = (firstStr + replaceStr).length;
  }
  return parentStr;
}

const linkifyText = (text) => {
  if (!text) return text;
  const urlRegex = /(\b(https?|ftp|file):\/\/[-A-Z0-9+&@#\/%?=~_|!:,.;]*[-A-Z0-9+&@#\/%=~_|])/ig;
  return text.replace(urlRegex, function(url) {
    return `<a href="${url}" target="_blank" rel="noopener noreferrer">${url}</a>`;
  });
};

// The ui_rules-driven processing for an assessment question: parses `ui_rules`,
// wraps TOOLTIP keywords in <tooltip> tags inside the question/choices, linkifies
// the top subtitle, and collects the USER_INPUT / UNSELECT index lists. Also owns
// the hover-tooltip state used by the tooltip overlay.
// This used to live inline inside AssessmentSlide.comp.jsx.
export const useAssessmentTooltips = ({ assessment, slide, options }) => {
  // Tooltip state
  const [tooltipText, setTooltipText] = useState('');
  const [displayTooltip, setDisplayTooltip] = useState(false);
  const [tooltipPos, setTooltipPos] = useState({ x: 0, y: 0 });

  // UI state
  const [processedQuestion, setProcessedQuestion] = useState('');
  const [processedChoices, setProcessedChoices] = useState([]);
  const [inputQuestions, setInputQuestions] = useState([]);
  const [unselectQuestions, setUnselectQuestions] = useState([]);
  const [processedSubtitleTop, setProcessedSubtitleTop] = useState('');

  const rules = useMemo(() => {
    if (!assessment.ui_rules) return [];

    try {
      const value = typeof assessment.ui_rules === "string" ? JSON.parse(assessment.ui_rules) : assessment.ui_rules;
      if (Array.isArray(value)) {
        return value;
      } else {
        console.error("uiRules is not an array after parsing:", value);
        return [];
      }
    } catch (e) {
      console.error("Failed to parse uiRules JSON:", assessment.ui_rules, e);
      return [];
    }
  }, [assessment]);


  // Process UI rules and question/choices
  useEffect(() => {

    console.log("rules:", rules, "type:", typeof rules);

    const inputIndices = [];
    const unselectIndices = [];
    let modifiedQuestion = assessment.instrument_question || slide.SlideTitle || '';
    let modifiedChoices = [...options];
    let modifiedSubtitleTop = assessment.question_subtitle_top;

    // Process UI rules
    (rules || []).forEach((rule) => {
      switch (rule.type) {
        case 'INDEX_EFFECT':
          if (rule.effect === 'USER_INPUT') {
            inputIndices.push(rule.index);
          } else if (rule.effect === 'UNSELECT') {
            unselectIndices.push(rule.index);
          }
          break;
        case 'QUESTION_EFFECT':
          if (rule.effect === 'TOOLTIP') {
            modifiedQuestion = replaceKeyword(
              rule.text,
              `<tooltip text='${rule.text}' html='${rule.html}'></tooltip>`,
              modifiedQuestion
            );
          }
          break;
        case 'ANSWER_EFFECT':
          if (rule.effect === 'TOOLTIP') {
            modifiedChoices = modifiedChoices.map((choice) =>
              replaceKeyword(
                rule.text,
                `<tooltip text='${rule.text}' html='${rule.html}'></tooltip>`,
                choice
              )
            );
          }
          break;
        default:
          break;
      }
    });

    // Linkify subtitle top
    modifiedSubtitleTop = linkifyText(modifiedSubtitleTop);

    setProcessedQuestion(modifiedQuestion);
    setProcessedChoices(modifiedChoices);
    setProcessedSubtitleTop(modifiedSubtitleTop);
    setInputQuestions(inputIndices);
    setUnselectQuestions(unselectIndices);
  }, [options, rules]);
  //}, [slideId, questionText, subtitleTop, JSON.stringify(options), rules]);

  // Tooltip handlers
  const onHoverTooltip = (event, text) => {
    setTooltipPos({ x: event.pageX, y: event.pageY });
    setTooltipText(text);
    setDisplayTooltip(true);
  };

  const onExitHoverTooltip = () => {
    setTooltipPos({ x: 0, y: 0 });
    setDisplayTooltip(false);
  };

  return {
    processedQuestion,
    processedChoices,
    processedSubtitleTop,
    inputQuestions,
    unselectQuestions,
    tooltipText,
    displayTooltip,
    tooltipPos,
    onHoverTooltip,
    onExitHoverTooltip
  };
};
